import React, { Component } from "react";
import Footer from "./Footer";
import "./Beyond-timeline.scss";

// Timeline Creative Design - Scroll-animated, Unique, Extremely Creative

class Beyond extends Component {
  componentDidMount() {
    window.scrollTo(0, 0);
    document.title = "Beyond Tech - Jerin Joseph Alour";
    
    // Intersection Observer for scroll animations
    this.setupScrollAnimations();
  }

  setupScrollAnimations = () => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('animate-in');
          }
        });
      },
      { threshold: 0.15, rootMargin: '0px 0px -50px 0px' }
    );

    setTimeout(() => {
      document.querySelectorAll('.timeline-item, .signal-card, .closing-section, .section-header.enhanced').forEach((el) => {
        observer.observe(el);
      });
    }, 100);
  };

  getTranslations() {
    // Get translations from props, or use default English
    return this.props.beyondData || {
      hero: {
        title: "Beyond",
        highlight: "Code",
        tagline: "Leadership • Impact • Growth",
        description: [
          "Some of the most meaningful work I've done never shipped as code.",
          "It happened in rooms full of people, conflicting priorities, and real consequences."
        ],
        scroll_text: "Scroll to explore my journey"
      },
      timeline: {
        section_title: "Where Responsibility Was Real",
        section_subtitle: "A journey through leadership, service, and growth",
        items: []
      },
      signals: {
        section_title: "Signals Along the Way",
        section_subtitle: "Recognition that marked milestones",
        items: []
      },
      closing: {
        quote: "Systems don't fail because people don't care—",
        quote_highlight: "they fail because they're not designed to listen.",
        note: "This page documents the work that taught me that lesson—slowly, honestly, and in full view of real people."
      },
      buttons: {
        view_certificate: "View Certificate",
        certificate: "Certificate"
      }
    };
  }

  // Timeline data with years - chronological order
  getTimelineData() {
    const translations = this.getTranslations();
    const baseData = [
      {
        id: 1,
        icon: "mdi:account-heart",
        certificate: "/certificates/social/Peer Educator - Certificate.pdf",
        image: process.env.PUBLIC_URL + "/images/beyond/badge-ceremony.jpg",
        color: "#00ACC1"
      },
      {
        id: 2,
        icon: "mdi:account-group",
        certificate: "/certificates/notable/SSC-School_Level_Student_Council_ChristUniversity.pdf",
        image: process.env.PUBLIC_URL + "/images/beyond/PhotoWithFatherSony_Campus Director.jpg",
        color: "#2196F3"
      },
      {
        id: 3,
        icon: "mdi:school",
        certificate: "/certificates/social/CSA- Certificate.pdf",
        color: "#4CAF50"
      },
      {
        id: 4,
        icon: "mdi:antenna",
        certificate: "/certificates/notable/IEEE_StudentBranch.pdf",
        color: "#9C27B0"
      },
      {
        id: 5,
        icon: "mdi:star-shooting",
        certificate: "/certificates/notable/Lead-Organised-DenearyLevelHead_Inter_University_Event_Magnovite.pdf",
        image: process.env.PUBLIC_URL + "/images/beyond/magnovite-speaking.jpg",
        imageType: "portrait",
        color: "#FF9800",
        featured: true
      }
    ];

    // Merge with translations
    return baseData.map((item, index) => ({
      ...item,
      ...translations.timeline.items[index]
    }));
  }

  // Signals/Recognition data
  getSignalsData() {
    const translations = this.getTranslations();
    const baseData = [
      {
        id: 6,
        icon: "mdi:trophy",
        certificate: "/certificates/notable/1stPlace_TechEvent.pdf",
        rank: 1,
        color: "#FFD700"
      },
      {
        id: 7,
        icon: "mdi:medal",
        certificate: "/certificates/notable/2ndPlace_NonTechEvent.pdf",
        rank: 2,
        color: "#C0C0C0"
      },
      {
        id: 8,
        icon: "mdi:lightbulb-on",
        certificate: "/certificates/notable/SAP_Workshop_Startup.pdf",
        rank: 3,
        color: "#CD7F32"
      }
    ];

    // Merge with translations
    return baseData.map((item, index) => ({
      ...item,
      ...translations.signals.items[index]
    }));
  }

  renderTimelineItem = (item, index) => {
    const isLeft = index % 2 === 0;
    const translations = this.getTranslations();
    
    return (
      <div key={item.id} className={`timeline-item ${isLeft ? 'left' : 'right'} ${item.featured ? 'featured' : ''}`}>
        <div className="timeline-marker">
          <div className="year-badge" style={{ backgroundColor: item.color }}>
            {item.year}
          </div>
          <div className="timeline-dot" style={{ borderColor: item.color, backgroundColor: item.color }}></div>
        </div>

        <div className="timeline-content">
          <div className="card-glow" style={{ background: `linear-gradient(135deg, ${item.color}15, transparent)` }}></div>
          
          <div className="card-icon" style={{ color: item.color }}>
            <span className="iconify" data-icon={item.icon} data-inline="false"></span>
          </div>

          {item.image && (
            <div className={`card-image ${item.imageType === 'portrait' ? 'portrait' : ''}`}>
              <img src={item.image} alt={item.title} onError={(e) => e.target.parentNode.style.display = 'none'} />
            </div>
          )}

          <div className="card-header">
            <h3>{item.title}</h3>
            <h4>{item.subtitle}</h4>
            <span className="organization">{item.organization}</span>
          </div>

          <div className="card-body">
            <p>{item.description}</p>
            
            {item.achievements && (
              <ul className="achievements">
                {item.achievements.map((a, i) => <li key={i}><span className="check">✓</span> {a}</li>)}
              </ul>
            )}
          </div>

          <blockquote className="insight" style={{ borderLeftColor: item.color }}>
            "{item.insight}"
          </blockquote>

          <a href={item.certificate} target="_blank" rel="noopener noreferrer" className="btn-certificate" style={{ backgroundColor: item.color }}>
            <span className="iconify" data-icon="mdi:certificate" data-inline="false"></span>
            {translations.buttons.view_certificate}
          </a>
        </div>
      </div>
    );
  };

  renderSignalCard = (item) => {
    const translations = this.getTranslations();
    return (
      <div key={item.id} className={`signal-card rank-${item.rank}`}>
        <div className="rank-ribbon" style={{ backgroundColor: item.color }}>
          <span>{item.rank}</span>
        </div>

        <div className="particles">
          {[...Array(6)].map((_, i) => (
            <div key={i} className="particle" style={{ animationDelay: `${i * 0.3}s` }}></div>
          ))}
        </div>

        <div className="signal-icon" style={{ color: item.color }}>
          <span className="iconify" data-icon={item.icon} data-inline="false"></span>
        </div>

        <div className="signal-content">
          <h3>{item.title}</h3>
          <h4>{item.subtitle}</h4>
          <p>{item.description}</p>
        </div>

        <a href={item.certificate} target="_blank" rel="noopener noreferrer" className="btn-signal" style={{ borderColor: item.color, color: item.color }}>
          <span className="iconify" data-icon="mdi:open-in-new" data-inline="false"></span>
          {translations.buttons.certificate}
        </a>

        <div className="corner-decoration" style={{ borderColor: item.color }}></div>
      </div>
    );
  };

  render() {
    const translations = this.getTranslations();
    const timelineData = this.getTimelineData();
    const signalsData = this.getSignalsData();

    return (
      <div id="beyond-page">
        {/* Hero Section */}
        <section className="hero-section">
          <div className="hero-bg-shapes">
            <div className="shape shape-1"></div>
            <div className="shape shape-2"></div>
            <div className="shape shape-3"></div>
            <div className="shape shape-4"></div>
          </div>
          
          <div className="hero-container">
            <div className="hero-badge">
              <span className="iconify" data-icon="mdi:rocket-launch" data-inline="false"></span>
            </div>
            <h1>{translations.hero.title} <span className="highlight">{translations.hero.highlight}</span></h1>
            <p className="hero-tagline">{translations.hero.tagline}</p>
            <div className="hero-description">
              {translations.hero.description.map((desc, i) => <p key={i}>{desc}</p>)}
            </div>
            <div className="scroll-indicator">
              <span className="iconify" data-icon="mdi:chevron-double-down" data-inline="false"></span>
              <span>{translations.hero.scroll_text}</span>
            </div>
          </div>
        </section>

        {/* Timeline Section */}
        <section className="timeline-section">
          <header className="section-header enhanced">
            <div className="floating-particles">
              <span className="particle"></span>
              <span className="particle"></span>
              <span className="particle"></span>
              <span className="particle"></span>
              <span className="particle"></span>
              <span className="particle"></span>
            </div>
            <div className="header-decoration">
              <span className="line animated-line"></span>
              <div className="icon-orbit">
                <span className="orbit-ring"></span>
                <span className="orbit-ring"></span>
                <span className="iconify" data-icon="mdi:timeline-clock" data-inline="false"></span>
              </div>
              <span className="line animated-line"></span>
            </div>
            <h2>
              <span className="text-reveal">{translations.timeline.section_title}</span>
              <span className="underline-glow"></span>
            </h2>
            <p className="section-subtitle">{translations.timeline.section_subtitle}</p>
            <div className="decorative-dots">
              <span></span><span></span><span></span>
            </div>
          </header>

          <div className="timeline-container">
            <div className="timeline-line"></div>
            {timelineData.map((item, index) => this.renderTimelineItem(item, index))}
          </div>
        </section>

        {/* Signals Section */}
        <section className="signals-section">
          <header className="section-header enhanced signals-header">
            <div className="sparkle-container">
              <span className="sparkle"></span>
              <span className="sparkle"></span>
              <span className="sparkle"></span>
              <span className="sparkle"></span>
              <span className="sparkle"></span>
              <span className="sparkle"></span>
              <span className="sparkle"></span>
              <span className="sparkle"></span>
            </div>
            <div className="header-decoration">
              <span className="line animated-line gold"></span>
              <div className="icon-orbit gold">
                <span className="orbit-ring"></span>
                <span className="orbit-ring"></span>
                <span className="iconify" data-icon="mdi:star-shooting" data-inline="false"></span>
              </div>
              <span className="line animated-line gold"></span>
            </div>
            <h2>
              <span className="text-reveal">{translations.signals.section_title}</span>
              <span className="underline-glow gold"></span>
            </h2>
            <p className="section-subtitle">{translations.signals.section_subtitle}</p>
            <div className="trophy-row">
              <span className="iconify trophy" data-icon="mdi:trophy" data-inline="false"></span>
              <span className="iconify medal" data-icon="mdi:medal" data-inline="false"></span>
              <span className="iconify trophy" data-icon="mdi:trophy-variant" data-inline="false"></span>
            </div>
          </header>

          <div className="signals-grid">
            {signalsData.map(item => this.renderSignalCard(item))}
          </div>
        </section>

        {/* Closing Section */}
        <section className="closing-section">
          <div className="closing-decoration">
            <span className="iconify" data-icon="mdi:format-quote-open" data-inline="false"></span>
          </div>
          <blockquote className="closing-quote">
            {translations.closing.quote}
            <strong>{translations.closing.quote_highlight}</strong>
          </blockquote>
          <p className="closing-note">
            {translations.closing.note}
          </p>
        </section>

        {/* Footer - same style as main page */}
        <Footer sharedBasicInfo={this.props.sharedBasicInfo} isBeyondPage={true} />
      </div>
    );
  }
}

export default Beyond;
